import React, { useCallback, useEffect, useState } from "react";
import { SYM } from "../utils/textSymbols";
import "./GmailTestingModal.css";

const TABS = [
  { id: "status", label: "Status & Poll" },
  { id: "prompts", label: "Prompt Config" },
  { id: "dryrun", label: "Dry Run" }
];

const TYPE_LABELS = {
  calendar: "Calendar",
  todo: "To-Do",
  grocery: "Grocery",
  recipe: "Recipe",
  school_schedule: "School Schedule"
};

function formatWatchExpiration(ms) {
  if (!ms) return "—";
  const n = Number(ms);
  if (!Number.isFinite(n)) return String(ms);
  return new Date(n).toLocaleString();
}

function formatLogTime(at) {
  if (!at) return "—";
  return new Date(at).toLocaleString();
}

function PushLogEntry({ log }) {
  const isError =
    log.outcome === "jwt_rejected" ||
    log.outcome === "auth_rejected" ||
    log.outcome === "error" ||
    log.outcome === "handler_error";
  const claims = log.jwt?.claims;
  return (
    <li className={`gmail-push-log-entry ${isError ? "gmail-push-log-error" : "gmail-push-log-ok"}`}>
      <span className="gmail-push-log-head">
        <strong>{log.outcome}</strong> · HTTP {log.httpStatus} · {formatLogTime(log.at)}
      </span>
      {log.jwtReason && <span className="gmail-push-log-detail">JWT: {log.jwtReason}</span>}
      {log.authReason && <span className="gmail-push-log-detail">Auth: {log.authReason}</span>}
      {log.authMethod && <span className="gmail-push-log-detail">Auth method: {log.authMethod}</span>}
      {log.authHint && <span className="gmail-push-log-detail">{log.authHint}</span>}
      {log.webhookReason && <span className="gmail-push-log-detail">Webhook: {log.webhookReason}</span>}
      {log.webhookSecretConfigured === false && (
        <span className="gmail-push-log-detail">GMAIL_PUSH_WEBHOOK_SECRET not set in Azure</span>
      )}
      {log.webhookKeyPresent === false && log.webhookSecretConfigured && (
        <span className="gmail-push-log-detail">No ?key= on push URL — update Pub/Sub subscription</span>
      )}
      {claims && (
        <span className="gmail-push-log-detail">
          Token claims — aud: {claims.aud || "—"}, email: {claims.email || claims.sub || "—"}
        </span>
      )}
      {log.jwt?.expectedAudiences?.length > 0 && (
        <span className="gmail-push-log-detail">
          Expected aud: {log.jwt.expectedAudiences.join(" or ")}
        </span>
      )}
      {log.jwt?.expectedServiceAccount && (
        <span className="gmail-push-log-detail">Expected SA: {log.jwt.expectedServiceAccount}</span>
      )}
      {log.jwt?.tokenPresent === false && (
        <span className="gmail-push-log-detail">No Authorization header (enable subscription auth)</span>
      )}
      {log.jwt?.tokeninfoStatus && log.jwt.tokeninfoStatus !== 200 && (
        <span className="gmail-push-log-detail">tokeninfo HTTP {log.jwt.tokeninfoStatus}</span>
      )}
      {log.poll && (
        <span className="gmail-push-log-detail">
          Unread catch-up · {log.poll.processed ?? 0} processed · {log.poll.skipped ?? 0} skipped
        </span>
      )}
      {log.push?.historyId && (
        <span className="gmail-push-log-detail">
          historyId {log.push.historyId}
          {log.push.newMessages != null ? ` · ${log.push.newMessages} new message(s)` : ""}
          {log.push.bootstrapped ? " · bootstrapped" : ""}
        </span>
      )}
      {log.error && <span className="gmail-push-log-detail">{log.error}</span>}
    </li>
  );
}

/* ----------------------------- Status & Poll ----------------------------- */

function StatusTab({ apiBase, status, statusLoading, statusError, loadStatus, onForbidden, onPollComplete }) {
  const [pollMax, setPollMax] = useState(10);
  const [pollLoading, setPollLoading] = useState(false);
  const [pollResult, setPollResult] = useState(null);
  const [pollError, setPollError] = useState(null);
  const [renewLoading, setRenewLoading] = useState(false);
  const [renewResult, setRenewResult] = useState(null);
  const [renewError, setRenewError] = useState(null);
  const [pushDryRun, setPushDryRun] = useState(false);
  const [pushViaHttp, setPushViaHttp] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [pushError, setPushError] = useState(null);
  const [pushLogs, setPushLogs] = useState([]);
  const [pushLogsLoading, setPushLogsLoading] = useState(false);
  const [pushLogsError, setPushLogsError] = useState(null);

  const loadPushLogs = useCallback(async () => {
    setPushLogsLoading(true);
    setPushLogsError(null);
    try {
      const res = await fetch(`${apiBase}/api/GetGmailPushLogs?limit=25`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) return onForbidden();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      setPushLogs(data.logs || []);
    } catch (err) {
      setPushLogsError(err.message || "Could not load push logs.");
    } finally {
      setPushLogsLoading(false);
    }
  }, [apiBase, onForbidden]);

  useEffect(() => {
    loadPushLogs();
  }, [loadPushLogs]);

  const runPoll = async () => {
    setPollLoading(true);
    setPollError(null);
    setPollResult(null);
    try {
      const res = await fetch(`${apiBase}/api/GmailPollInbox`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ max: pollMax, process: true, skipProcessed: true })
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) return onForbidden();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      setPollResult(data);
      onPollComplete?.();
      await loadStatus();
    } catch (err) {
      setPollError(err.message || "Poll failed.");
    } finally {
      setPollLoading(false);
    }
  };

  const runRenewWatch = async () => {
    setRenewLoading(true);
    setRenewError(null);
    setRenewResult(null);
    try {
      const res = await fetch(`${apiBase}/api/GmailWatchRenew`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) return onForbidden();
      if (!res.ok) throw new Error(data.error || data.reason || `Error ${res.status}`);
      setRenewResult(data);
      await loadStatus();
    } catch (err) {
      setRenewError(err.message || "Watch renew failed.");
    } finally {
      setRenewLoading(false);
    }
  };

  const runSimulatePush = async () => {
    setPushLoading(true);
    setPushError(null);
    setPushResult(null);
    try {
      const res = await fetch(`${apiBase}/api/GmailSimulatePush`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ dryRun: pushDryRun, viaHttp: pushViaHttp })
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) return onForbidden();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      setPushResult(data);
      if (!pushDryRun && !pushViaHttp) onPollComplete?.();
      await loadStatus();
    } catch (err) {
      setPushError(err.message || "Simulate push failed.");
    } finally {
      setPushLoading(false);
    }
  };

  return (
    <>
      <p className="settings-hint">
        Verify Gmail access, manually poll the inbox, and check Pub/Sub push configuration. Poll
        processes allowlisted senders only (same rules as live push).
      </p>

      <div className="gmail-testing-actions">
        <button type="button" className="settings-submit" disabled={statusLoading} onClick={loadStatus}>
          {statusLoading ? "Checking…" : "Test access"}
        </button>
        {status?.connected && (
          <a
            href={`${apiBase}/api/GmailOAuthStart`}
            className={`settings-link-btn ${status.needsReauth || status.accessOk === false ? "settings-submit" : "settings-btn-secondary"}`}
          >
            Reconnect Gmail
          </a>
        )}
        <button
          type="button"
          className="settings-btn-secondary"
          disabled={renewLoading || !status?.configured}
          onClick={runRenewWatch}
        >
          {renewLoading ? "Renewing…" : "Renew watch"}
        </button>
        <button
          type="button"
          className="settings-btn-secondary"
          disabled={pushLogsLoading}
          onClick={loadPushLogs}
        >
          {pushLogsLoading ? "Loading…" : "Refresh push logs"}
        </button>
      </div>

      {statusError && <p className="settings-error">{statusError}</p>}
      {renewError && <p className="settings-error">{renewError}</p>}
      {renewResult?.ok && (
        <p className="settings-success">Watch renewed. Expires {formatWatchExpiration(renewResult.expiration)}.</p>
      )}

      {status && (
        <div className="gmail-testing-status">
          <h3>Connection</h3>
          <ul>
            <li>Connected: {status.connected ? "Yes" : "No"}</li>
            <li>API access: {status.accessOk ? "OK" : status.accessError || "Failed"}</li>
            {(status.needsReauth || status.accessOk === false) && (
              <li className="gmail-testing-warning">
                Authentication needs a refresh. Click Reconnect Gmail to grant a new token (Google testing-mode tokens expire after 7 days).
              </li>
            )}
            <li>Inbox: {status.email || "—"}</li>
            <li>Push enabled: {status.pushEnabled ? "Yes" : "No"}</li>
            {status.pushEndpoint && <li>Push endpoint: {status.pushEndpoint}</li>}
            {status.pubsubPushUrlHint && (
              <li>Pub/Sub URL (use in GCP): {status.pubsubPushUrlHint}</li>
            )}
            {status.webhookAuthConfigured === false && (
              <li className="gmail-testing-warning">
                GMAIL_PUSH_WEBHOOK_SECRET not set — live push auth will fail on Azure SWA
              </li>
            )}
            <li>Watch expires: {formatWatchExpiration(status.watchExpiration)}</li>
            {status.watchActive === false && (
              <li className="gmail-testing-warning">Watch expired — live push will not fire until you renew</li>
            )}
            <li>History cursor: {status.lastHistoryId || "—"}</li>
            {status.liveHistoryId && <li>Live Gmail historyId: {status.liveHistoryId}</li>}
            {status.historyBehind === true && (
              <li className="gmail-testing-warning">
                Gmail is ahead of stored cursor — mail may be waiting for a push
              </li>
            )}
            {status.pubsubServiceAccount && (
              <li>Pub/Sub service account: {status.pubsubServiceAccount}</li>
            )}
            <li>Allowed senders: {(status.allowedSenders || []).join(", ") || "(none)"}</li>
          </ul>
          {status.pubsubChecklist?.length > 0 && (
            <>
              <h3>Pub/Sub checklist</h3>
              <ul className="gmail-testing-checklist">
                {status.pubsubChecklist.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </>
          )}
          {status.pubsubNote && <p className="settings-hint">{status.pubsubNote}</p>}

          {status.recentInbox?.length > 0 && (
            <>
              <h3>Recent inbox</h3>
              <ul className="gmail-testing-inbox-list">
                {status.recentInbox.map((msg) => (
                  <li key={msg.id}>
                    <span className="gmail-testing-subject">{msg.subject}</span>
                    <span className="gmail-testing-from">{msg.fromEmail || msg.from}</span>
                    {msg.unread && <span className="gmail-testing-unread">unread</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="gmail-testing-poll">
        <h3>Poll inbox</h3>
        <p className="settings-hint">
          Fetches recent INBOX messages and runs the email processor on any not yet handled. This
          creates real approvals. After deploy, GitHub Actions also polls unread mail hourly.
        </p>
        <label htmlFor="gmail-poll-max">Max messages</label>
        <input
          id="gmail-poll-max"
          type="number"
          min={1}
          max={25}
          value={pollMax}
          onChange={(e) => setPollMax(Math.min(25, Math.max(1, parseInt(e.target.value, 10) || 10)))}
          className="settings-input gmail-poll-max-input"
        />
        <button
          type="button"
          className="settings-submit"
          disabled={pollLoading || !status?.accessOk}
          onClick={runPoll}
        >
          {pollLoading ? "Polling…" : "Poll & process"}
        </button>
        {pollError && <p className="settings-error">{pollError}</p>}
        {pollResult && (
          <div className="gmail-testing-poll-result">
            {pollResult.error && (
              <p className="settings-error">Inbox error: {pollResult.error} (showing partial results)</p>
            )}
            <p className="settings-success">
              Listed {pollResult.listed}, processed {pollResult.processed}, skipped {pollResult.skipped}
              {pollResult.errors ? `, errors ${pollResult.errors}` : ""}.
            </p>
            {pollResult.results?.length > 0 && (
              <ul className="gmail-testing-poll-list">
                {pollResult.results.map((r) => (
                  <li key={r.messageId}>
                    <strong>{r.action}</strong>: {r.subject}
                    {r.outcome?.created?.length ? ` → created ${r.outcome.created.join(", ")}` : ""}
                    {r.outcome?.skipped ? ` (${r.outcome.reason || r.reason})` : ""}
                    {r.error ? ` — ${r.error}` : ""}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="gmail-testing-push">
        <h3>Simulate Pub/Sub push</h3>
        <p className="settings-hint">
          Runs the same code path as a real Google Pub/Sub delivery using Gmail&apos;s current
          historyId. Use this to verify push processing without waiting for live delivery.
        </p>
        <div className="gmail-testing-push-options">
          <label>
            <input
              type="checkbox"
              checked={pushDryRun}
              onChange={(e) => setPushDryRun(e.target.checked)}
            />{" "}
            Dry run (preview only — do not advance history cursor)
          </label>
          <label>
            <input
              type="checkbox"
              checked={pushViaHttp}
              onChange={(e) => setPushViaHttp(e.target.checked)}
            />{" "}
            POST via HTTP endpoint (tests routing/JWT on {status?.pushEndpoint || "production URL"})
          </label>
        </div>
        <button
          type="button"
          className="settings-submit"
          disabled={pushLoading || !status?.accessOk}
          onClick={runSimulatePush}
        >
          {pushLoading ? "Simulating…" : pushViaHttp ? "Test HTTP push endpoint" : "Simulate push now"}
        </button>
        {pushError && <p className="settings-error">{pushError}</p>}
        {pushResult && (
          <div className="gmail-testing-push-result">
            {pushResult.diagnostics && (
              <ul className="gmail-testing-checklist">
                <li>Mode: {pushResult.diagnostics.mode}</li>
                <li>Simulated historyId: {pushResult.diagnostics.simulatedHistoryId}</li>
                <li>Stored cursor: {pushResult.diagnostics.storedHistoryId || "—"}</li>
                {pushResult.diagnostics.historyBehind && (
                  <li className="gmail-testing-warning">History behind live Gmail state</li>
                )}
              </ul>
            )}
            {pushResult.http && (
              <p className={pushResult.http.status === 200 ? "settings-success" : "settings-error"}>
                HTTP {pushResult.http.status}:{" "}
                {pushResult.http.body?.reason || pushResult.http.body?.error || JSON.stringify(pushResult.http.body)}
              </p>
            )}
            {pushResult.push && (
              <>
                {pushResult.push.bootstrapped && (
                  <p className="settings-hint">
                    Bootstrapped cursor to {pushResult.push.historyId} (no mail processed on first push).
                  </p>
                )}
                {pushResult.push.reset && (
                  <p className="settings-hint">History expired — cursor reset to {pushResult.push.historyId}.</p>
                )}
                {pushResult.push.newMessages != null && (
                  <p className="settings-success">
                    New INBOX messages: {pushResult.push.newMessages}
                    {pushResult.push.dryRun ? " (dry run)" : ""}
                  </p>
                )}
                {pushResult.push.results?.length > 0 && (
                  <ul className="gmail-testing-poll-list">
                    {pushResult.push.results.map((r) => (
                      <li key={r.messageId}>
                        <strong>{r.action || "processed"}</strong>: {r.subject || r.messageId}
                        {r.outcome?.created?.length ? ` → created ${r.outcome.created.join(", ")}` : ""}
                        {r.outcome?.skipped ? ` (${r.outcome.reason || r.reason})` : ""}
                        {r.error ? ` — ${r.error}` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="gmail-testing-push-logs">
        <h3>Live push log</h3>
        <p className="settings-hint">
          Each Google Pub/Sub delivery to /api/GmailPush is stored in Cosmos (last 25). Use this to
          see JWT rejection reasons without Application Insights.
        </p>
        {pushLogsError && <p className="settings-error">{pushLogsError}</p>}
        {pushLogs.length === 0 && !pushLogsLoading && (
          <p className="settings-hint">No push events yet. Send a test email, then refresh.</p>
        )}
        {pushLogs.length > 0 && (
          <ul className="gmail-push-log-list">
            {pushLogs.map((log) => (
              <PushLogEntry key={log.id} log={log} />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

/* ----------------------------- Prompt Config ----------------------------- */

function PromptsTab({ apiBase, onForbidden }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/GetEmailAiConfig`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) return onForbidden();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      setConfig(data.config);
    } catch (err) {
      setError(err.message || "Could not load prompt config.");
    } finally {
      setLoading(false);
    }
  }, [apiBase, onForbidden]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const res = await fetch(`${apiBase}/api/UpdateEmailAiConfig`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ config })
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) return onForbidden();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      setConfig(data.config);
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const setField = (path, value) => {
    setSuccess(false);
    setConfig((c) => {
      const next = { ...c };
      if (path[0] === "perType") next.perType = { ...c.perType, [path[1]]: value };
      else if (path[0] === "defaults") next.defaults = { ...c.defaults, [path[1]]: value };
      else next[path[0]] = value;
      return next;
    });
  };

  const updateRule = (idx, field, value) => {
    setSuccess(false);
    setConfig((c) => {
      const rules = [...(c.senderRules || [])];
      rules[idx] = { ...rules[idx], [field]: value };
      return { ...c, senderRules: rules };
    });
  };

  const addRule = () => {
    setConfig((c) => ({ ...c, senderRules: [...(c.senderRules || []), { match: "", guidance: "" }] }));
  };

  const removeRule = (idx) => {
    setConfig((c) => ({ ...c, senderRules: (c.senderRules || []).filter((_, i) => i !== idx) }));
  };

  if (loading || !config) {
    return <p className="settings-hint">{loading ? "Loading…" : error || "No config."}</p>;
  }

  return (
    <>
      <p className="settings-hint">
        Steer how the AI interprets family email. Guidance is injected into the classifier prompt.
      </p>

      <label htmlFor="ai-global">Global guidance</label>
      <textarea
        id="ai-global"
        className="settings-input gmail-config-textarea"
        rows={3}
        placeholder="e.g. We are the Clark family. School emails are usually calendar events."
        value={config.globalGuidance || ""}
        onChange={(e) => setField(["globalGuidance"], e.target.value)}
      />

      <h3>Per-type guidance</h3>
      {["calendar", "todo", "grocery", "recipe"].map((t) => (
        <div key={t} className="gmail-config-pertype">
          <label htmlFor={`ai-${t}`}>{TYPE_LABELS[t]}</label>
          <textarea
            id={`ai-${t}`}
            className="settings-input gmail-config-textarea"
            rows={2}
            placeholder={`Guidance for ${TYPE_LABELS[t].toLowerCase()} emails`}
            value={config.perType?.[t] || ""}
            onChange={(e) => setField(["perType", t], e.target.value)}
          />
        </div>
      ))}

      <h3>Sender rules</h3>
      <p className="settings-hint">
        Match by exact email, <code>@domain.com</code>, <code>domain.com</code>, or <code>*</code>.
      </p>
      {(config.senderRules || []).map((rule, idx) => (
        <div key={idx} className="gmail-config-rule">
          <input
            className="settings-input gmail-config-rule-match"
            placeholder="spa@example.com"
            value={rule.match}
            onChange={(e) => updateRule(idx, "match", e.target.value)}
          />
          <input
            className="settings-input gmail-config-rule-guidance"
            placeholder="These are appointments; merge multi-person PDFs into one event."
            value={rule.guidance}
            onChange={(e) => updateRule(idx, "guidance", e.target.value)}
          />
          <button type="button" className="gmail-config-rule-remove" onClick={() => removeRule(idx)} aria-label="Remove rule">
            {SYM.close}
          </button>
        </div>
      ))}
      <button type="button" className="settings-btn-secondary" onClick={addRule}>
        + Add sender rule
      </button>

      <h3>Defaults</h3>
      <label htmlFor="ai-default-assignee">Default task assignee (when none named)</label>
      <input
        id="ai-default-assignee"
        className="settings-input"
        placeholder="All"
        value={config.defaults?.todoAssignee || ""}
        onChange={(e) => setField(["defaults", "todoAssignee"], e.target.value)}
      />
      <label htmlFor="ai-default-grocery">Default grocery list (optional)</label>
      <input
        id="ai-default-grocery"
        className="settings-input"
        placeholder="(none)"
        value={config.defaults?.groceryList || ""}
        onChange={(e) => setField(["defaults", "groceryList"], e.target.value)}
      />

      <div className="gmail-testing-actions">
        <button type="button" className="settings-submit" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save prompt config"}
        </button>
      </div>
      {error && <p className="settings-error">{error}</p>}
      {success && <p className="settings-success">Prompt config saved.</p>}
    </>
  );
}

/* ----------------------------- Dry Run ----------------------------- */

function formatEventWhen(ev) {
  if (!ev) return "";
  const timed = ev.startTime && ev.startTime !== "00:00" && ev.allDay !== true;
  if (timed) {
    const end =
      ev.endTime && ev.endTime !== "00:00" && ev.endTime !== ev.startTime ? `–${ev.endTime}` : "";
    return `${ev.startDate} ${ev.startTime}${end}`;
  }
  return `${ev.startDate} (all day)`;
}

function IntentCard({ intent }) {
  return (
    <div className="gmail-dryrun-intent">
      <div className="gmail-dryrun-intent-head">
        <span className="gmail-dryrun-type">{TYPE_LABELS[intent.type] || intent.type}</span>
        <span className="gmail-dryrun-summary">{intent.summary}</span>
      </div>
      {Array.isArray(intent.events) &&
        intent.events.map((ev, i) => (
          <div key={i} className="gmail-dryrun-detail">
            <strong>{ev.title}</strong> · {formatEventWhen(ev)}
            {ev.location ? ` · ${ev.location}` : ""}
            {ev.participants?.length ? ` · ${ev.participants.join(", ")}` : ""}
          </div>
        ))}
      {Array.isArray(intent.items) &&
        intent.items.map((it, i) => (
          <div key={i} className="gmail-dryrun-detail">
            {it.title || it.ingredient}
            {it.dueDate ? ` · due ${it.dueDate}` : ""}
            {it.assignee ? ` · ${it.assignee}` : ""}
            {it.important ? " · ⚑ important" : ""}
            {it.quantity ? ` · ${it.quantity}` : ""}
            {it.listName ? ` · list: ${it.listName}` : ""}
          </div>
        ))}
    </div>
  );
}

function DryRunTab({ apiBase, status, onForbidden }) {
  const [mode, setMode] = useState("inbox");
  const [messageId, setMessageId] = useState("");
  const [subject, setSubject] = useState("");
  const [rawText, setRawText] = useState("");
  const [from, setFrom] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const inbox = status?.recentInbox || [];

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const payload =
        mode === "inbox" ? { messageId } : { rawText, subject, from };
      const res = await fetch(`${apiBase}/api/GmailClassifyPreview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) return onForbidden();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      setResult(data);
    } catch (err) {
      setError(err.message || "Classification failed.");
    } finally {
      setLoading(false);
    }
  };

  const canRun = mode === "inbox" ? !!messageId : !!rawText.trim();

  return (
    <>
      <p className="settings-hint">
        Preview what the AI would extract — no approvals, labels, or calendar/todo writes are created.
      </p>

      <div className="gmail-dryrun-mode">
        <label>
          <input type="radio" checked={mode === "inbox"} onChange={() => setMode("inbox")} /> Recent inbox
        </label>
        <label>
          <input type="radio" checked={mode === "paste"} onChange={() => setMode("paste")} /> Paste text
        </label>
      </div>

      {mode === "inbox" ? (
        <>
          <label htmlFor="dryrun-msg">Message</label>
          <select
            id="dryrun-msg"
            className="settings-input"
            value={messageId}
            onChange={(e) => setMessageId(e.target.value)}
          >
            <option value="">Select a recent message…</option>
            {inbox.map((m) => (
              <option key={m.id} value={m.id}>
                {m.subject} — {m.fromEmail || m.from}
              </option>
            ))}
          </select>
          {inbox.length === 0 && (
            <p className="settings-hint">Run “Test access” on the Status tab to load recent messages.</p>
          )}
        </>
      ) : (
        <>
          <label htmlFor="dryrun-from">From (optional, for sender rules)</label>
          <input
            id="dryrun-from"
            className="settings-input"
            placeholder="sender@example.com"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <label htmlFor="dryrun-subject">Subject</label>
          <input
            id="dryrun-subject"
            className="settings-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <label htmlFor="dryrun-body">Email body</label>
          <textarea
            id="dryrun-body"
            className="settings-input gmail-config-textarea"
            rows={6}
            placeholder="Paste the email text here…"
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
          />
        </>
      )}

      <div className="gmail-testing-actions">
        <button type="button" className="settings-submit" disabled={loading || !canRun} onClick={run}>
          {loading ? "Classifying…" : "Run dry run"}
        </button>
      </div>
      {error && <p className="settings-error">{error}</p>}

      {result && (
        <div className="gmail-dryrun-result">
          <ul className="gmail-dryrun-meta">
            {result.from && <li>From: {result.from}</li>}
            {result.allowlisted != null && (
              <li>Allowlisted: {result.allowlisted ? "Yes" : "No (would be skipped in live processing)"}</li>
            )}
            <li>Steering applied: {result.steeringApplied ? "Yes" : "No"}</li>
            {result.matchedSenderRules?.length > 0 && (
              <li>Matched sender rules: {result.matchedSenderRules.length}</li>
            )}
            {result.source === "inbox" && <li>Attachments detected: {result.attachmentCount ?? 0}</li>}
            {result.attachmentEvents?.length > 0 && (
              <li>Events from attachments: {result.attachmentEvents.length}</li>
            )}
          </ul>

          {result.source === "inbox" && (
            <>
              <h3>Attachments ({result.attachmentCount ?? 0})</h3>
              {(result.attachments || []).length === 0 ? (
                <p className="settings-hint">
                  {result.attachmentCount
                    ? "Attachments detected but none were processed."
                    : "No attachments found on this message."}
                </p>
              ) : (
                <ul className="gmail-dryrun-attach-list">
                  {result.attachments.map((a, i) => (
                    <li key={i} className={`gmail-dryrun-attach gmail-dryrun-attach-${a.status}`}>
                      <span className="gmail-dryrun-attach-name">{a.filename}</span>
                      <span className="gmail-dryrun-attach-meta">
                        {a.mimeType || "unknown"} · {Math.round((a.size || 0) / 1024)} KB
                      </span>
                      <span className="gmail-dryrun-attach-flags">
                        {a.downloaded ? "downloaded" : "not downloaded"}
                        {a.handler ? ` · ${a.handler}` : ""}
                        {a.uploadedToAi ? " · sent to AI" : ""}
                        {` · ${a.status}`}
                        {a.events ? ` · ${a.events} event(s)` : ""}
                      </span>
                      {a.error && <span className="gmail-dryrun-attach-error">{a.error}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          <h3>Intents ({result.intents?.length || 0})</h3>
          {(result.intents || []).length === 0 ? (
            <p className="settings-hint">No actionable intents detected.</p>
          ) : (
            result.intents.map((intent, i) => <IntentCard key={i} intent={intent} />)
          )}

          {result.attachmentEvents?.length > 0 && (
            <>
              <h3>Attachment events</h3>
              {result.attachmentEvents.map((ev, i) => (
                <div key={i} className="gmail-dryrun-detail">
                  <strong>{ev.title}</strong> · {formatEventWhen(ev)}
                  {ev.participants?.length ? ` · ${ev.participants.join(", ")}` : ""}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </>
  );
}

/* ----------------------------- Modal shell ----------------------------- */

export default function GmailTestingModal({ apiBase, open, onClose, onPollComplete }) {
  const [tab, setTab] = useState("status");
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const res = await fetch(`${apiBase}/api/GmailTestAccess`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) {
        setForbidden(true);
        return;
      }
      if (!res.ok) throw new Error(data.error || data.message || `Error ${res.status}`);
      setStatus(data);
    } catch (err) {
      setStatusError(err.message || "Could not load Gmail status.");
    } finally {
      setStatusLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (!open) return;
    setTab("status");
    setForbidden(false);
    loadStatus();
  }, [open, loadStatus]);

  if (!open) return null;

  const onForbidden = () => setForbidden(true);

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-modal gmail-testing-modal" onClick={(e) => e.stopPropagation()}>
        <div className="settings-modal-header">
          <h2>Gmail testing &amp; AI config</h2>
          <button type="button" className="settings-close" onClick={onClose} aria-label="Close">
            {SYM.close}
          </button>
        </div>

        {forbidden ? (
          <div className="settings-modal-body">
            <p className="settings-error">Only admins can run Gmail tests.</p>
          </div>
        ) : (
          <>
            <div className="gmail-testing-tabs">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`gmail-testing-tab ${tab === t.id ? "active" : ""}`}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="settings-modal-body gmail-testing-body">
              {tab === "status" && (
                <StatusTab
                  apiBase={apiBase}
                  status={status}
                  statusLoading={statusLoading}
                  statusError={statusError}
                  loadStatus={loadStatus}
                  onForbidden={onForbidden}
                  onPollComplete={onPollComplete}
                />
              )}
              {tab === "prompts" && <PromptsTab apiBase={apiBase} onForbidden={onForbidden} />}
              {tab === "dryrun" && <DryRunTab apiBase={apiBase} status={status} onForbidden={onForbidden} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
